import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Collapse,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Toolbar,
  Tooltip
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { strings } from "../../../localization/LocalizedStrings";
import { useUploadImage } from "../../../hooks/images";
import { TextField } from "../../TextField";
import { DatePickerField } from "../../DatePickerField";
import { IArtist } from "../../../hooks/artists";
import { ILabel } from "../../../hooks/labels";
import { IStore } from "../../../hooks/stores";
import { useAddAlbum } from "../../../hooks/albums/useAddAlbum";
import { ArtistsDesktop } from "../../attributes/artists/ArtistsDesktop";
import { IAlbumFormat } from "../../../hooks/albumFormats";
import { IAlbumType } from "../../../hooks/albumTypes";
import { LabelsDesktop } from "../../attributes/labels/LabelsDesktop";
import { AlbumFormatsDesktop } from "../../attributes/albumFormats/AlbumFormatsDesktop";
import { AlbumTypesDesktop } from "../../attributes/albumTypes/AlbumTypesDesktop";
import { Counter } from "../../Counter";
import { StoresDesktop } from "../../attributes/stores/StoresDesktop";
import { BooleanAttributesListItem } from "../../attributes/BooleanAttributesListItem";
import { ISellValue } from "../../../hooks/sellValues";
import { SellValuesDesktop } from "../../attributes/sellValues/SellValuesDesktop";
import { ISleeveGrade } from "../../../hooks/sleeveGrades";
import { IMediaGrade } from "../../../hooks/mediaGrades";
import { SleeveGradesDesktop } from "../../attributes/sleeveGrades/SleeveGradesDesktop";
import { MediaGradesDesktop } from "../../attributes/mediaGrades/MediaGradesDesktop";
import { StatusCodes } from "http-status-codes";
import { AlertDialog } from "../../AlertDialog";
import { ImageDropzone } from "../../ImageDropzone";
import { LengthTextField } from "../../LengthTextField";
import { ICollection } from "../../../hooks/collections";
import { CollectionsDialog } from "../../collections/CollectionsDialog";
import { toast } from "react-toastify";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { ImageCache } from "../../../models/ImageCache";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { useUserProfile } from "../../../hooks/session";
import { IAlbum } from "../../../hooks/albums";
import { useAddAlbumContent } from "../../../hooks/albumContents/useAddAlbumContent";
import { Firebase } from "../../../services/Firebase";
import { ProductPurchasesDialog } from "../../products/ProductPurchasesDialog";
import { ProductType } from "../../../hooks/products/interfaces";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { AttributeDialog } from "../../attributes/AttributeDialog";
import { OriginCountriesDesktop } from "../../attributes/originCountries/OriginCountriesDesktop";
import { IOriginCountry } from "../../../hooks/originCountries";
import { PiperDialog } from "../../PiperDialog";
import { Icon, IconType } from "../../../icons";

export const AddAlbumDialog: FunctionComponent<IAddAlbumDialogProps> = ({
  open,
  setOpen,
  callback,
  isBoxSet
}) => {
  const isMobile = useIsMobile();
  const [currentUserProfile] = useUserProfile.useState();
  const { addAlbum } = useAddAlbum();
  const { uploadImage } = useUploadImage();
  const { addAlbumContent } = useAddAlbumContent();
  const [isLoading, setIsLoading] = useState(false);
  const [didChangeImage, setDidChangeImage] = useState(false);
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false);
  const [isArtistDesktopOpen, setIsArtistDesktopOpen] = useState(false);
  const [isLabelDesktopOpen, setIsLabelDesktopOpen] = useState(false);
  const [isAlbumFormatDesktopOpen, setIsAlbumFormatDesktopOpen] = useState(false);
  const [isAlbumTypeDesktopOpen, setIsAlbumTypeDesktopOpen] = useState(false);
  const [isStoreDesktopOpen, setIsStoreDesktopOpen] = useState(false);
  const [isSellValueDesktopOpen, setIsSellValueDesktopOpen] = useState(false);
  const [isSleeveGradeDesktopOpen, setIsSleeveGradeDesktopOpen] = useState(false);
  const [isMediaGradeDesktopOpen, setIsMediaGradeDesktopOpen] = useState(false);
  const [isOriginCountriesDesktopOpen, setIsOriginCountriesDesktopOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File>();
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const genericSleeveImage = new URL("../../../images/generic_sleeve.jpg", import.meta.url).href;
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      handleClose();
    }
  };
  const handleClose = () => {
    setOpen(false);
    setImageFile(undefined);
    formik.resetForm();
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing_short),
    purchasePrice: Yup.number().typeError(strings.field_invalid)
  });
  const initialValues: IFormProps = {
    name: "",
    artist: undefined,
    originCountry: undefined,
    originalYear: "",
    releaseYear: "",
    label: undefined,
    catalogueNumber: "",
    albumFormat: undefined,
    albumType: undefined,
    color: "",
    discCount: undefined,
    store: undefined,
    discogsId: "",
    length: undefined,
    trackCount: undefined,
    collection: undefined,
    isClean: false,
    hasLyrics: false,
    hasInsert: false,
    isGatefold: false,
    purchasePrice: "",
    sellValue: undefined,
    sleeveGrade: undefined,
    mediaGrade: undefined,
    purchaseDate: new Date()
  };
  const formik = useFormik({
    initialValues: initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      setIsLoading(true);
      let uuid = undefined;
      const { status, body } = await addAlbum({
        name: values.name,
        albumTypeId: values.albumType?.uuid,
        artistId: values.artist?.uuid,
        catalogueNumber: values.catalogueNumber,
        collections: values.collection ? [values.collection] : undefined,
        originCountryId: values.originCountry?.uuid,
        discogsReleaseId: values.discogsId,
        hasInsert: values.hasInsert,
        hasLyrics: values.hasLyrics,
        impactId: undefined,
        isClean: values.isClean,
        isGatefold: values.isGatefold,
        labelId: values.label?.uuid,
        length: values.length,
        mediaGradeId: values.mediaGrade?.id,
        order: -1,
        originalYear: values.originalYear == "" ? undefined : values.originalYear,
        purchaseDate: values.purchaseDate
          ? Math.floor(values.purchaseDate.getTime() / 1000)
          : undefined,
        purchasePrice: values.purchasePrice ? +values.purchasePrice : undefined,
        releaseYear: values.releaseYear == "" ? undefined : values.releaseYear,
        sellValueId: values.sellValue?.uuid,
        sleeveGradeId: values.sleeveGrade?.id,
        storeId: values.store?.uuid,
        tracks: values.trackCount,
        isBoxSet: isBoxSet
      });
      if (status == StatusCodes.CREATED) {
        uuid = body.uuid;
        if (didChangeImage && imageFile && uuid) {
          const thumbnail = await uploadImage(
            { name: uuid, file: imageFile },
            ImageCache.bigAlbumImageSize,
            ImageCache.bigAlbumImageSize,
            ImageCache.smallAlbumImageSize,
            ImageCache.smallAlbumImageSize
          );
          if (thumbnail) {
            body.hasImage = true;
            body.thumbnail = thumbnail;
          }
        }
        if (values.discCount || values.albumFormat || values.color) {
          // Create an album content
          await addAlbumContent({
            albumId: uuid,
            albumFormatId: values.albumFormat?.uuid,
            color: values.color,
            quantity: values.discCount,
            type: "disc"
          });
        }
        toast.success(strings.album_created);
        Firebase.shared.logFirebaseEvent("new_album_manual", {
          user_id: currentUserProfile.userId,
          album_id: uuid
        });
        callback?.(body);
        handleClose();
      } else if (StatusCodesHelper.isLimitReached(status)) {
        setAlertMessage(body.message);
        setIsPurchaseAlertOpen(true);
      } else {
        setAlertMessage(body.message);
        setIsAlertOpen(true);
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  return (
    <FormikProvider value={formik}>
      <PiperDialog open={open} onClose={tryToClose} maxWidth={"lg"}>
        <Toolbar>
          <h3>{isBoxSet ? strings.new_box_set_title : strings.ca_title}</h3>
          <Box flexGrow={1} />
          <ToolbarButton
            href={`${window.location.protocol}//${
              window.location.host
            }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.home(
              "create"
            )}`}
            target={"_blank"}
            tooltip={strings.help}
            iconType={IconType.Help}
          />
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent
          dividers
          sx={{
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: isMobile ? 1 : 2,
            paddingRight: isMobile ? 1 : 2
          }}
        >
          <Stack direction={"row"} spacing={3} sx={{ marginTop: 1 }}>
            <Stack>
              <FieldTitle className={styles.itemTitle}>{strings.front_cover}</FieldTitle>
              <ImageDropzone
                className={styles.dropzone}
                placeholderImage={genericSleeveImage}
                onImageChanged={file => {
                  setImageFile(file);
                  setDidChangeImage(true);
                }}
                isLoading={false}
              />
            </Stack>
            <Stack sx={{ width: "100%" }}>
              <FieldTitle className={styles.itemTitle}>{strings.name_title}*</FieldTitle>
              <div className={styles.textField}>
                <TextField
                  id="name"
                  name="name"
                  size="small"
                  autoComplete="albumName"
                  placeholder={
                    isBoxSet ? strings.box_set_name_placeholder : strings.name_placeholder
                  }
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  autoFocus={!isMobile}
                />
              </div>
              {!isBoxSet && (
                <>
                  <FieldTitle className={styles.itemTitle}>{strings.artist_title}</FieldTitle>
                  <div className={styles.textField}>
                    <TextField
                      id="artist"
                      name="artist"
                      size="small"
                      placeholder={strings.artist_placeholder}
                      value={formik.values.artist?.name ?? ""}
                      readOnly
                      onClick={() => setIsArtistDesktopOpen(true)}
                      enterAction={() => setIsArtistDesktopOpen(true)}
                    />
                  </div>
                </>
              )}
            </Stack>
          </Stack>
          <Collapse in={isExpanded} timeout={"auto"}>
            <Grid container spacing={1}>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.origin_country_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="country"
                    name="country"
                    size="small"
                    readOnly
                    placeholder={strings.origin_country_title}
                    value={formik.values.originCountry?.name ?? ""}
                    onClick={() => setIsOriginCountriesDesktopOpen(true)}
                    enterAction={() => setIsOriginCountriesDesktopOpen(true)}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.original_year_title}</FieldTitle>
                <div className={styles.textField}>
                  <DatePickerField
                    title={strings.original_year_title}
                    value={
                      formik.values.originalYear
                        ? new Date(`${formik.values.originalYear}-06`)
                        : null
                    }
                    onChange={date => {
                      const originalYear =
                        date == null ? null : (date as unknown as Date).getFullYear();
                      formik
                        .setFieldValue("originalYear", originalYear ? String(originalYear) : null)
                        .then();
                    }}
                    format={"yyyy"}
                    views={["year"]}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.release_year_title}</FieldTitle>
                <div className={styles.textField}>
                  <DatePickerField
                    title={strings.release_year_title}
                    value={
                      formik.values.releaseYear ? new Date(`${formik.values.releaseYear}-06`) : null
                    }
                    onChange={date => {
                      const releaseYear =
                        date == null ? null : (date as unknown as Date).getFullYear();
                      formik
                        .setFieldValue("releaseYear", releaseYear ? String(releaseYear) : null)
                        .then();
                    }}
                    format={"yyyy"}
                    views={["year"]}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.label_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="label"
                    name="label"
                    size="small"
                    readOnly
                    placeholder={strings.ml_placeholder}
                    value={formik.values.label?.name ?? ""}
                    onClick={() => setIsLabelDesktopOpen(true)}
                    enterAction={() => setIsLabelDesktopOpen(true)}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>
                  {strings.catalogue_number_title}
                </FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="catalogueNumber"
                    name="catalogueNumber"
                    size="small"
                    autoComplete="catalogueNumber"
                    placeholder={strings.catalogue_number_placeholder}
                    value={formik.values.catalogueNumber}
                    onChange={formik.handleChange}
                  />
                </div>
              </Grid>
              {!isBoxSet && (
                <Grid item xs className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>{strings.album_format_title}</FieldTitle>
                  <div className={styles.textField}>
                    <TextField
                      id="albumFormat"
                      name="albumFormat"
                      size="small"
                      readOnly
                      placeholder={strings.maf_short_name_placeholder}
                      value={formik.values.albumFormat?.name ?? ""}
                      onClick={() => setIsAlbumFormatDesktopOpen(true)}
                      enterAction={() => setIsAlbumFormatDesktopOpen(true)}
                    />
                  </div>
                </Grid>
              )}
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.album_type_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="albumType"
                    name="albumType"
                    size="small"
                    readOnly
                    placeholder={strings.mat_placeholder}
                    value={formik.values.albumType?.name ?? ""}
                    onClick={() => setIsAlbumTypeDesktopOpen(true)}
                    enterAction={() => setIsAlbumTypeDesktopOpen(true)}
                  />
                </div>
              </Grid>
              {!isBoxSet && (
                <Grid item xs className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>
                    {strings.disc_color_singular_title}
                  </FieldTitle>
                  <div className={styles.textField}>
                    <TextField
                      id="color"
                      name="color"
                      size="small"
                      autoComplete="color"
                      placeholder={strings.disc_color_placeholder}
                      value={formik.values.color}
                      onChange={formik.handleChange}
                    />
                  </div>
                </Grid>
              )}
              {!isBoxSet && (
                <Grid item xs className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>{strings.disc_count_title}</FieldTitle>
                  <Counter
                    className={styles.counter}
                    initialValue={0}
                    minimumValue={1}
                    nullable={true}
                    onValueChanged={newValue => formik.setFieldValue("discCount", newValue)}
                  />
                </Grid>
              )}
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.store_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="store"
                    name="store"
                    size="small"
                    readOnly
                    placeholder={strings.ms_placeholder}
                    value={formik.values.store?.name ?? ""}
                    onClick={() => setIsStoreDesktopOpen(true)}
                    enterAction={() => setIsStoreDesktopOpen(true)}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.discogs_id_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="discogsId"
                    name="discogsId"
                    autoComplete="discogsId"
                    size="small"
                    placeholder={strings.ma_discogs_id_placeholder}
                    value={formik.values.discogsId}
                    onChange={formik.handleChange}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.purchase_date_title}</FieldTitle>
                <div className={styles.textField}>
                  <DatePickerField
                    title={strings.purchase_date_title}
                    value={formik.values.purchaseDate ?? null}
                    onChange={newValue => formik.setFieldValue("purchaseDate", newValue)}
                  />
                </div>
              </Grid>
              {!isBoxSet && (
                <Grid item xs className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>{strings.track_count_title}</FieldTitle>
                  <Counter
                    className={styles.counter}
                    initialValue={0}
                    minimumValue={1}
                    nullable={true}
                    onValueChanged={newValue => formik.setFieldValue("trackCount", newValue)}
                  />
                </Grid>
              )}
              {!isBoxSet && (
                <Grid item xs className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>{strings.album_length_title}</FieldTitle>
                  <div className={styles.textField}>
                    <LengthTextField
                      id={"length"}
                      name={"length"}
                      size={"small"}
                      onChange={newValue => formik.setFieldValue("length", newValue)}
                      defaultValue={formik.values.length}
                    />
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={1}>
              <Grid item sm className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>
                  {isBoxSet ? strings.sleeve_grade_box_set_title : strings.sleeve_grade_title}
                </FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="sleeveGrade"
                    name="sleeveGrade"
                    size="small"
                    readOnly
                    placeholder={strings.sleeve_grade_placeholder}
                    value={formik.values.sleeveGrade?.goldmineGrade ?? ""}
                    onClick={() => setIsSleeveGradeDesktopOpen(true)}
                    enterAction={() => setIsSleeveGradeDesktopOpen(true)}
                  />
                </div>
              </Grid>
              {!isBoxSet && (
                <Grid item sm className={styles.itemContainer}>
                  <FieldTitle className={styles.itemTitle}>
                    {strings.media_grade_singular_title}
                  </FieldTitle>
                  <div className={styles.textField}>
                    <TextField
                      id="mediaGrade"
                      name="mediaGrade"
                      size="small"
                      readOnly
                      placeholder={strings.media_grade_placeholder}
                      value={formik.values.mediaGrade?.goldmineGrade ?? ""}
                      onClick={() => setIsMediaGradeDesktopOpen(true)}
                      enterAction={() => setIsMediaGradeDesktopOpen(true)}
                    />
                  </div>
                </Grid>
              )}
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.collection_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="collection"
                    name="collection"
                    size="small"
                    readOnly
                    placeholder={strings.collection_name_placeholder}
                    value={formik.values.collection?.name ?? ""}
                    onClick={() => setIsCollectionsDialogOpen(true)}
                    enterAction={() => setIsCollectionsDialogOpen(true)}
                  />
                </div>
              </Grid>
            </Grid>
            <BooleanAttributesListItem
              isPublic={false}
              className={styles.booleansSection}
              titleWeight={"bold"}
              hideIsGatefold={isBoxSet}
              hideIsClean={false}
              isCleanLoading={false}
              isGatefoldLoading={false}
              hasLyricsLoading={false}
              hasInsertLoading={false}
              isGatefold={formik.values.isGatefold}
              hasInsert={formik.values.hasInsert}
              hasLyrics={formik.values.hasLyrics}
              isClean={formik.values.isClean}
              onIsGatefoldChanged={newValue => formik.setFieldValue("isGatefold", newValue)}
              onHasInsertChanged={newValue => formik.setFieldValue("hasInsert", newValue)}
              onHasLyricsChanged={newValue => formik.setFieldValue("hasLyrics", newValue)}
              onIsCleanChanged={newValue => formik.setFieldValue("isClean", newValue)}
            />
            <Grid container spacing={1}>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.purchase_price_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="purchasePrice"
                    name="purchasePrice"
                    size="small"
                    autoComplete="purchasePrice"
                    placeholder={strings.purchase_price_placeholder}
                    value={formik.values.purchasePrice}
                    onChange={formik.handleChange}
                  />
                </div>
              </Grid>
              <Grid item xs className={styles.itemContainer}>
                <FieldTitle className={styles.itemTitle}>{strings.sell_value_title}</FieldTitle>
                <div className={styles.textField}>
                  <TextField
                    id="sellValue"
                    name="sellValue"
                    size="small"
                    readOnly
                    placeholder={strings.sell_value_title}
                    value={formik.values.sellValue?.group ?? ""}
                    onClick={() => setIsSellValueDesktopOpen(true)}
                    enterAction={() => setIsSellValueDesktopOpen(true)}
                  />
                </div>
              </Grid>
            </Grid>
          </Collapse>
          <div className={styles.expandIconDiv}>
            <Tooltip
              title={isExpanded ? strings.shrink : strings.optional_attributes}
              arrow
              disableInteractive
            >
              <IconButton onClick={() => setIsExpanded(state => !state)}>
                {isExpanded ? (
                  <Icon type={IconType.KeyboardArrowUp} />
                ) : (
                  <Icon type={IconType.KeyboardArrowDown} />
                )}
              </IconButton>
            </Tooltip>
          </div>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AttributeDialog open={isArtistDesktopOpen} onClose={() => setIsArtistDesktopOpen(false)}>
        <ArtistsDesktop
          handleClose={() => {
            setIsArtistDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={formik.values.artist?.uuid}
          onArtistSelected={async artist => {
            setIsArtistDesktopOpen(false);
            await formik.setFieldValue("artist", artist);
          }}
          onClearValue={async () => {
            setIsArtistDesktopOpen(false);
            await formik.setFieldValue("artist", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog open={isLabelDesktopOpen} onClose={() => setIsLabelDesktopOpen(false)}>
        <LabelsDesktop
          handleClose={() => {
            setIsLabelDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={formik.values.label?.uuid}
          onLabelSelected={async label => {
            setIsLabelDesktopOpen(false);
            await formik.setFieldValue("label", label);
          }}
          onClearValue={async () => {
            setIsLabelDesktopOpen(false);
            await formik.setFieldValue("label", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isAlbumFormatDesktopOpen}
        onClose={() => setIsAlbumFormatDesktopOpen(false)}
      >
        <AlbumFormatsDesktop
          handleClose={() => {
            setIsAlbumFormatDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 4}
          preselectedId={formik.values.albumFormat?.uuid}
          onAlbumFormatSelected={async albumFormat => {
            setIsAlbumFormatDesktopOpen(false);
            await formik.setFieldValue("albumFormat", albumFormat);
          }}
          onClearValue={async () => {
            setIsAlbumFormatDesktopOpen(false);
            await formik.setFieldValue("albumFormat", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isAlbumTypeDesktopOpen}
        onClose={() => setIsAlbumTypeDesktopOpen(false)}
      >
        <AlbumTypesDesktop
          handleClose={() => {
            setIsAlbumTypeDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 4}
          preselectedId={formik.values.albumType?.uuid}
          onAlbumTypeSelected={async albumType => {
            setIsAlbumTypeDesktopOpen(false);
            await formik.setFieldValue("albumType", albumType);
          }}
          onClearValue={async () => {
            setIsAlbumTypeDesktopOpen(false);
            await formik.setFieldValue("albumType", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog open={isStoreDesktopOpen} onClose={() => setIsStoreDesktopOpen(false)}>
        <StoresDesktop
          handleClose={() => {
            setIsStoreDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 6 : 4}
          preselectedId={formik.values.store?.uuid}
          onStoreSelected={async store => {
            setIsStoreDesktopOpen(false);
            await formik.setFieldValue("store", store);
          }}
          onClearValue={async () => {
            setIsStoreDesktopOpen(false);
            await formik.setFieldValue("store", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isOriginCountriesDesktopOpen}
        onClose={() => setIsOriginCountriesDesktopOpen(false)}
      >
        <OriginCountriesDesktop
          handleClose={() => {
            setIsOriginCountriesDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 6 : 4}
          preselectedId={formik.values.originCountry?.uuid}
          onOriginCountrySelected={async originCountry => {
            setIsOriginCountriesDesktopOpen(false);
            await formik.setFieldValue("originCountry", originCountry);
          }}
          onClearValue={async () => {
            setIsOriginCountriesDesktopOpen(false);
            await formik.setFieldValue("originCountry", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isSellValueDesktopOpen}
        onClose={() => setIsSellValueDesktopOpen(false)}
      >
        <SellValuesDesktop
          handleClose={() => {
            setIsSellValueDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 6 : 4}
          preselectedId={formik.values.sellValue?.uuid}
          onSellValueSelected={async sellValue => {
            setIsSellValueDesktopOpen(false);
            await formik.setFieldValue("sellValue", sellValue);
          }}
          onClearValue={async () => {
            setIsSellValueDesktopOpen(false);
            await formik.setFieldValue("sellValue", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isSleeveGradeDesktopOpen}
        onClose={() => setIsSleeveGradeDesktopOpen(false)}
      >
        <SleeveGradesDesktop
          handleClose={() => {
            setIsSleeveGradeDesktopOpen(false);
          }}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={formik.values.sleeveGrade?.id}
          onSleeveGradeSelected={async sleeveGrade => {
            setIsSleeveGradeDesktopOpen(false);
            await formik.setFieldValue("sleeveGrade", sleeveGrade);
          }}
          onClearValue={async () => {
            setIsSleeveGradeDesktopOpen(false);
            await formik.setFieldValue("sleeveGrade", undefined);
          }}
        />
      </AttributeDialog>
      <AttributeDialog
        open={isMediaGradeDesktopOpen}
        onClose={() => setIsMediaGradeDesktopOpen(false)}
      >
        <MediaGradesDesktop
          handleClose={() => {
            setIsMediaGradeDesktopOpen(false);
          }}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={formik.values.mediaGrade?.id}
          onMediaGradeSelected={async mediaGrade => {
            setIsMediaGradeDesktopOpen(false);
            await formik.setFieldValue("mediaGrade", mediaGrade);
          }}
          onClearValue={async () => {
            setIsMediaGradeDesktopOpen(false);
            await formik.setFieldValue("mediaGrade", undefined);
          }}
        />
      </AttributeDialog>
      <CollectionsDialog
        open={isCollectionsDialogOpen}
        setOpen={setIsCollectionsDialogOpen}
        isLayout={false}
        canModifyCollections={false}
        displayStandardCollections={false}
        onCollectionSelected={async collection => {
          await formik.setFieldValue("collection", collection);
        }}
        selectedCollections={[]}
        forceLoading={false}
        canNegate={false}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={handleClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUMS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

interface IFormProps {
  name: string;
  artist?: IArtist;
  originCountry?: IOriginCountry;
  originalYear?: string;
  releaseYear?: string;
  label?: ILabel;
  catalogueNumber: string;
  albumFormat?: IAlbumFormat;
  albumType?: IAlbumType;
  color?: string;
  discCount?: number;
  store?: IStore;
  discogsId?: string;
  length?: number;
  trackCount?: number;
  isClean: boolean;
  hasInsert: boolean;
  hasLyrics: boolean;
  isGatefold: boolean;
  purchasePrice?: string;
  sellValue?: ISellValue;
  sleeveGrade?: ISleeveGrade;
  mediaGrade?: IMediaGrade;
  purchaseDate: Date;
  collection?: ICollection;
}

export interface IAddAlbumDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  callback?: (newAlbum: IAlbum) => void;
  isBoxSet: boolean;
}
